import { useQuery } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

import { useCustomersSelectQuery } from '@/features/customers/hooks'
import { usePackageCategoriesSelectQuery } from '@/features/package-categories/hooks'
import { usePartnersSelectQuery } from '@/features/partners/hooks'
import { useShipmentCategoriesSelectQuery } from '@/features/shipment-categories/hooks'
import { getSitesByDcIdSelectQueryOptions } from '@/features/sites/query-options'
import { useTruckModelsSelectQuery } from '@/features/truck-models/hooks'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { type MasterCandidatesByField } from '../models'

/**
 * マスタ参照項目（荷主/配送種別/発着地/パートナー/車種/荷姿）の実マスタ検索候補。
 *
 * 使う読み取りAPIは既存の各Select系（他画面の CustomerSelect 等と共有）で、
 * 汎用CSVインポート専用の API は使わない。
 *
 * 一括入力パネル（PreviewView）とマスタ参照存在チェック（preview.ts buildPreview）が
 * 同じ実データを共有する。
 */
export const useMasterReferenceCandidates = (
  tenantKey: string
): { candidates: MasterCandidatesByField; isLoading: boolean } => {
  const dcId = useAtomValue(selectedDcIdAtom)
  const disabled = !tenantKey || dcId === null

  const customers = useCustomersSelectQuery(tenantKey, {
    dcIds: dcId !== null ? [dcId] : undefined,
    disabled
  })
  const shipmentCategories = useShipmentCategoriesSelectQuery(tenantKey, {
    dcId: dcId ?? undefined,
    disabled
  })
  const sites = useQuery({
    ...getSitesByDcIdSelectQueryOptions({ tenantKey, dcId: dcId ?? 0 }),
    enabled: !disabled
  })
  const partners = usePartnersSelectQuery(tenantKey, {
    dcId: dcId ?? undefined,
    disabled
  })
  const packageCategories = usePackageCategoriesSelectQuery(tenantKey, {
    dcId: dcId ?? undefined,
    disabled
  })
  // 車種は取込が照合する車種マスタ（TruckModel、拠点・有効で絞る）から取る。
  // 車格マスタ（TruckClass）とはコードの体系が別で、取り違えると正しい車種コードが未登録扱いになる
  const truckModels = useTruckModelsSelectQuery(tenantKey, {
    dcId: dcId ?? undefined,
    disabled
  })

  const candidates = useMemo<MasterCandidatesByField>(() => {
    const toCandidates = (
      list: ReadonlyArray<{ code: string; name: string }> | undefined
    ) => list?.map((item) => ({ code: item.code, name: item.name }))

    const siteCandidates = toCandidates(sites.data)

    return {
      customerId: toCandidates(customers.data),
      shipmentCategoryId: toCandidates(shipmentCategories.data),
      originSiteId: siteCandidates,
      destSiteId: siteCandidates,
      partnerId: toCandidates(partners.data),
      truckModelId: toCandidates(truckModels.data),
      packageCategoryId: toCandidates(packageCategories.data)
    }
  }, [
    customers.data,
    shipmentCategories.data,
    sites.data,
    partners.data,
    packageCategories.data,
    truckModels.data
  ])

  const isLoading =
    !disabled &&
    (customers.isLoading ||
      shipmentCategories.isLoading ||
      sites.isLoading ||
      partners.isLoading ||
      packageCategories.isLoading ||
      truckModels.isLoading)

  return { candidates, isLoading }
}
